import React, { useEffect, useMemo, useRef, useState } from 'react';
import { usePeriod } from '../lib/period';
import { api, fmtNum, MONTHS_ID } from '../lib/api';
import { StatusBadge } from '../components/StatusBadge';
import { ExportButton, ExportGabunganButton, DeletePeriodButton } from '../components/PeriodActions';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../components/ui/tooltip';
import { toast } from 'sonner';
import { Search, RefreshCw, Info, Wand2, Pencil, ArrowUpDown, ArrowUp, ArrowDown, CalendarPlus, SlidersHorizontal } from 'lucide-react';

const FILTERS = [
  { key: 'all', label: 'Semua', cls: 'data-[active=true]:bg-primary data-[active=true]:text-primary-foreground' },
  { key: 'critical', label: 'Kritis', cls: 'data-[active=true]:bg-red-600 data-[active=true]:text-white' },
  { key: 'warning', label: 'Waspada', cls: 'data-[active=true]:bg-amber-500 data-[active=true]:text-slate-950' },
  { key: 'safe', label: 'Aman', cls: 'data-[active=true]:bg-emerald-600 data-[active=true]:text-white' },
  { key: 'unknown', label: 'Perlu Cek', cls: 'data-[active=true]:bg-slate-500 data-[active=true]:text-white' },
];

function EditableNumber({ value, onSave, testid, alignCls = 'text-right', valueCls = '', marker = null, title, editable = true }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState('');
  const inputRef = useRef(null);

  const start = () => {
    if (!editable) return;
    setVal(value === null || value === undefined ? '' : String(value));
    setEditing(true);
  };
  useEffect(() => { if (editing && inputRef.current) inputRef.current.select(); }, [editing]);

  const commit = () => {
    setEditing(false);
    const trimmed = val.trim();
    const parsed = trimmed === '' ? null : Number(trimmed);
    if (trimmed !== '' && Number.isNaN(parsed)) { toast.error('Nilai harus angka'); return; }
    const current = value === null || value === undefined ? null : Number(value);
    if (parsed === current) return;
    onSave(parsed);
  };

  if (editing) {
    return (
      <td className={`border-b px-1 py-1 ${alignCls}`}>
        <input
          ref={inputRef}
          type="number"
          data-testid={`${testid}-input`}
          className="num h-7 w-16 rounded border border-primary bg-background px-1 text-right text-sm outline-none ring-2 ring-ring"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            else if (e.key === 'Escape') setEditing(false);
          }}
        />
      </td>
    );
  }

  const tooltipText = editable ? (title || 'Klik untuk edit') : title;

  return (
    <td
      className={`num ${editable ? 'group/edit cursor-pointer hover:bg-primary/5' : ''} border-b px-2 py-1.5 ${alignCls} ${valueCls}`}
      data-testid={testid}
      onClick={start}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex items-center gap-1 cursor-pointer">
            {marker}
            {fmtNum(value)}
            {editable && <Pencil className="h-3 w-3 opacity-0 text-muted-foreground transition-opacity group-hover/edit:opacity-70" />}
          </span>
        </TooltipTrigger>
        {tooltipText && <TooltipContent><p>{tooltipText}</p></TooltipContent>}
      </Tooltip>
    </td>
  );
}

function EditableDay({ value, onSave, testid, editable, isOverridden, isToday, day, isDerived }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState('');
  const inputRef = useRef(null);

  const start = () => {
    if (!editable) return;
    setVal(value === null || value === undefined ? '' : String(value));
    setEditing(true);
  };
  useEffect(() => { if (editing && inputRef.current) inputRef.current.select(); }, [editing]);

  const commit = () => {
    setEditing(false);
    const trimmed = val.trim();
    const parsed = trimmed === '' ? null : Number(trimmed);
    if (trimmed !== '' && Number.isNaN(parsed)) { toast.error('Nilai harus angka'); return; }
    const current = value === null || value === undefined ? null : Number(value);
    if (parsed === current) return;
    onSave(parsed);
  };

  if (editing) {
    return (
      <td className={`w-8 min-w-[32px] max-w-[32px] text-center border-b py-1 px-0 ${isToday ? 'ls-today' : ''}`}>
        <input
          ref={inputRef}
          type="number"
          data-testid={`${testid}-input`}
          className="num h-6 w-full rounded border border-primary bg-background px-0 text-center text-[11px] outline-none ring-1 ring-ring"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            else if (e.key === 'Escape') setEditing(false);
          }}
        />
      </td>
    );
  }

  const tooltipText = isOverridden 
    ? `Hari ${day}: nilai manual (klik untuk edit)` 
    : (isDerived 
        ? `Hari ${day}: default otomatis = Quantiferon Tube × 4 (klik untuk edit manual)` 
        : `Hari ${day}: nilai dari data LIS (klik untuk edit manual)`);

  return (
    <td
      className={`w-8 min-w-[32px] max-w-[32px] text-center border-b py-1.5 ${editable ? 'cursor-pointer hover:bg-primary/10' : ''} ${isOverridden ? 'font-bold text-amber-600' : (value ? '' : 'text-muted-foreground/40')} ${isToday ? 'ls-today' : ''}`}
      data-testid={testid}
      onClick={start}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="block w-full h-full cursor-pointer">
            {value || ''}
          </span>
        </TooltipTrigger>
        <TooltipContent><p>{tooltipText}</p></TooltipContent>
      </Tooltip>
    </td>
  );
}

export default function PemantauanStok() {
  const { year, month, setYear, setMonth, refreshPeriods } = usePeriod();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [autoLoading, setAutoLoading] = useState(false);
  const [newPeriodOpen, setNewPeriodOpen] = useState(false);
  const [newPeriodLoading, setNewPeriodLoading] = useState(false);
  const [sortDir, setSortDir] = useState('asc');

  // =========================================================
  // STATE FITUR QUICK STOCK ADJUST (OPSI A - STOK SISTEM)
  // =========================================================
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [realStockVal, setRealStockVal] = useState('');
  const [adjustReason, setAdjustReason] = useState('Dead Volume / Sisa Botol');
  const [adjustNote, setAdjustNote] = useState('');
  const [adjustLoading, setAdjustLoading] = useState(false);

  const nextPeriod = month === 12 ? { year: year + 1, month: 1 } : { year, month: month + 1 };
  const nextLabel = `${MONTHS_ID[nextPeriod.month]} ${nextPeriod.year}`;

  const runNewPeriod = async () => {
    setNewPeriodLoading(true);
    try {
      const res = await api.buatPeriodeBaru(year, month);
      toast.success(`Periode ${res.label} dibuat: saldo awal ${res.reagen} reagen diambil dari sisa stok ${res.from_period}`);
      setNewPeriodOpen(false);
      await refreshPeriods();
      setYear(res.year);
      setMonth(res.month);
    } catch (e) {
      toast.error('Gagal membuat periode baru');
    } finally {
      setNewPeriodLoading(false);
    }
  };

  const load = () => {
    setLoading(true);
    setError(null);
    api.monitoring(year, month)
      .then((d) => setData(d))
      .catch(() => setError('Gagal memuat data pemantauan.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month]);

  const days = data?.days || 31;
  const dayCols = useMemo(() => Array.from({ length: days }, (_, i) => i + 1), [days]);
  const now = new Date();
  const shownYear = data?.year ?? year;
  const shownMonth = data?.month ?? month;
  const todayCol = now.getFullYear() === Number(shownYear) && now.getMonth() + 1 === Number(shownMonth) ? now.getDate() : null;

  const calculateQcValue = (item) => {
    if (!item || !item.nama_reagen) return item?.qc || 0;

    const reagenNameUpper = item.nama_reagen.toUpperCase();
    const isIndiko = reagenNameUpper.includes('INDIKO');

    if (isIndiko) {
      let qcCount = 0;
      const isLdlCholesterol = reagenNameUpper.includes('LDL CHOLESTEROL');
      const incrementValue = isLdlCholesterol ? 1 : 2;

      for (let day = 1; day <= 31; day++) {
        const val =
          (item.hari && item.hari[String(day)]) ||
          (item.hari && item.hari[day]) ||
          item[String(day)] ||
          item[day] ||
          item[`day_${day}`];

        if (val !== undefined && val !== null && val !== '' && Number(val) > 0) {
          qcCount += incrementValue;
        }
      }
      return qcCount;
    }

    return item.qc !== undefined && item.qc !== null ? item.qc : 0;
  };

  const rows = useMemo(() => {
    if (!data) return [];
    const list = data.rows.filter((r) => {
      if (filter !== 'all' && r.status !== filter) return false;
      if (q && !r.nama_reagen.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
    if (sortDir) {
      list.sort((a, b) => a.nama_reagen.localeCompare(b.nama_reagen, 'id', { sensitivity: 'base' }));
      if (sortDir === 'desc') list.reverse();
    }
    return list;
  }, [data, filter, q, sortDir]);

  const toggleSort = () => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
  const SortIcon = sortDir === 'asc' ? ArrowUp : sortDir === 'desc' ? ArrowDown : ArrowUpDown;

  const saveSaldo = async (row, value) => {
    try {
      await api.setSaldoAwal({ reagen_id: row.reagen_id, year, month, saldo_awal: value });
      toast.success(`Saldo awal "${row.nama_reagen}" diperbarui`);
      load();
    } catch (e) { toast.error('Gagal menyimpan saldo awal'); }
  };

  const saveQc = async (row, value) => {
    try {
      await api.setQc({ reagen_id: row.reagen_id, year, month, qc: value });
      toast.success(`QC "${row.nama_reagen}" diperbarui`);
      load();
    } catch (e) { toast.error('Gagal menyimpan QC'); }
  };

  const saveHari = async (row, day, value) => {
    try {
      await api.setHariOverride({ reagen_id: row.reagen_id, year, month, day, value });
      toast.success(`Nilai hari ${day} untuk "${row.nama_reagen}" diperbarui`);
      load();
    } catch (e) { toast.error('Gagal menyimpan nilai harian'); }
  };

  // =========================================================
  // HANDLERS QUICK STOCK ADJUST (OPSI A - STOK SISTEM)
  // =========================================================
  const handleOpenAdjust = (row, calculatedStock) => {
    setSelectedRow({ ...row, currentCalculatedStock: calculatedStock });
    setRealStockVal(String(calculatedStock));
    setAdjustReason('Dead Volume / Sisa Botol');
    setAdjustNote('');
    setAdjustOpen(true);
  };

  const handleSaveAdjust = async () => {
    if (!selectedRow) return;
    const trimmed = realStockVal.trim();
    if (trimmed === '') {
      toast.error('Nilai stok riil harus diisi');
      return;
    }
    const targetStock = Number(trimmed);
    if (Number.isNaN(targetStock) || targetStock < 0) {
      toast.error('Nilai stok riil harus angka positif yang valid');
      return;
    }

    setAdjustLoading(true);
    try {
      const delta = targetStock - selectedRow.currentCalculatedStock;
      await api.adjustStock({
        reagen_id: selectedRow.reagen_id,
        year,
        month,
        new_stock: targetStock,
        delta,
        reason: adjustReason,
        note: adjustNote,
      });

      toast.success(`Stok "${selectedRow.nama_reagen}" berhasil disesuaikan menjadi ${targetStock}`);
      setAdjustOpen(false);
      load();
    } catch (e) {
      toast.error('Gagal menyimpan koreksi stok');
    } finally {
      setAdjustLoading(false);
    }
  };

  const markedDaysSet = useMemo(() => new Set(data?.marked_days || []), [data]);

  const toggleMark = async (day) => {
    const isMarked = markedDaysSet.has(day);
    try {
      await api.toggleTanggalMark({ year, month, day, marked: !isMarked });
      load();
    } catch (e) { toast.error('Gagal menyimpan tanda tanggal'); }
  };

  const runAuto = async () => {
    setAutoLoading(true);
    try {
      const res = await api.autoSaldoAwal(year, month);
      toast.success(`Saldo awal ${res.updated} reagen diisi dari ${res.from_period}`);
      load();
    } catch (e) {
      toast.error('Gagal mengisi saldo awal otomatis');
    } finally {
      setAutoLoading(false);
    }
  };

  return (
    <TooltipProvider>
      <div className="space-y-4">
        {/* Header + filters */}
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="text-sm text-muted-foreground">Pemantauan pemakaian & stok reagen</div>
            <div className="text-lg font-semibold">{data?.label || `${month}/${year}`}</div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                data-testid="pemantauan-search"
                placeholder="Cari reagen..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="h-9 w-[200px] pl-8"
              />
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="sm" onClick={runAuto} disabled={autoLoading} data-testid="auto-saldo-awal-button">
                  <Wand2 className="mr-1.5 h-3.5 w-3.5" /> {autoLoading ? 'Memproses...' : 'Saldo Awal Otomatis'}
                </Button>
              </TooltipTrigger>
              <TooltipContent><p>Isi Saldo Awal = Sisa Stok bulan sebelumnya</p></TooltipContent>
            </Tooltip>

            <AlertDialog open={newPeriodOpen} onOpenChange={setNewPeriodOpen}>
              <AlertDialogTrigger asChild>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button size="sm" data-testid="new-period-button">
                      <CalendarPlus className="mr-1.5 h-3.5 w-3.5" /> Periode Bulan Baru
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent><p>{`Buat periode ${nextLabel}`}</p></TooltipContent>
                </Tooltip>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="new-period-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>Buat periode {nextLabel}?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Saldo Awal setiap reagen pada <b>{nextLabel}</b> akan diisi dari Sisa Stok <b>{data?.label || `${month}/${year}`}</b> saat ini.
                    Jika periode {nextLabel} sudah ada, saldo awalnya akan ditimpa dengan nilai terbaru.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="new-period-cancel">Batal</AlertDialogCancel>
                  <AlertDialogAction onClick={(e) => { e.preventDefault(); runNewPeriod(); }} disabled={newPeriodLoading} data-testid="new-period-confirm">
                    {newPeriodLoading ? 'Memproses...' : `Buat ${nextLabel}`}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <ExportButton year={year} month={month} />
            <ExportGabunganButton year={year} month={month} />
            <DeletePeriodButton year={year} month={month} label={data?.label || `${month}/${year}`} />
            <Button variant="outline" size="sm" onClick={load} data-testid="pemantauan-reload-button">
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Muat Ulang
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2" data-testid="status-alert-strip">
          {FILTERS.map((f) => {
            const count = f.key === 'all' ? data?.total_reagen : data?.counts?.[f.key];
            return (
              <button
                key={f.key}
                data-testid={`status-filter-chip-${f.key}`}
                data-active={filter === f.key}
                onClick={() => setFilter(f.key)}
                className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors hover:bg-muted ${f.cls}`}
              >
                {f.label}{count !== undefined ? ` (${count})` : ''}
              </button>
            );
          })}
        </div>

        {loading && (
          <Card className="p-4" data-testid="pemantauan-loading">
            <Skeleton className="mb-3 h-8 w-full" />
            {Array.from({ length: 10 }).map((_, i) => (
              <Skeleton key={i} className="mb-2 h-6 w-full" />
            ))}
          </Card>
        )}

        {!loading && error && (
          <Card className="p-8 text-center" data-testid="pemantauan-error">
            <p className="text-sm text-destructive">{error}</p>
            <Button className="mt-3" onClick={load}>Coba Lagi</Button>
          </Card>
        )}

        {!loading && !error && data && rows.length === 0 && (
          <Card className="p-10 text-center" data-testid="pemantauan-empty-state">
            <p className="text-sm text-muted-foreground">Belum ada data untuk periode / filter ini.</p>
          </Card>
        )}

        {!loading && !error && data && rows.length > 0 && (
          <>
            <Card className="relative overflow-hidden p-0">
              <div className="ls-scroll" style={{ maxHeight: 'calc(100vh - 260px)' }}>
                <table className="w-full border-collapse text-sm" data-testid="pemantauan-table">
                  <thead>
                    <tr className="text-xs uppercase tracking-wide text-muted-foreground">
                      <th className="ls-sticky-col border-b border-r px-3 py-2 text-left min-w-[220px]">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button onClick={toggleSort} data-testid="sort-nama-reagen" className="inline-flex items-center gap-1 uppercase tracking-wide hover:text-foreground">
                              Nama Reagen <SortIcon className="h-3.5 w-3.5" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent><p>Urutkan berdasarkan Nama Reagen</p></TooltipContent>
                        </Tooltip>
                      </th>
                      <th className="w-20 min-w-[75px] border-b px-2 py-2 text-center text-xs font-semibold uppercase tracking-wider leading-tight whitespace-pre-line">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="cursor-pointer">{"Saldo\nAwal ✎"}</span>
                          </TooltipTrigger>
                          <TooltipContent><p>Klik nilai untuk edit manual, atau gunakan tombol Saldo Awal Otomatis</p></TooltipContent>
                        </Tooltip>
                      </th>
                      {dayCols.map((d) => {
                        const isMarked = markedDaysSet.has(d);
                        const headerTooltip = isMarked 
                          ? `Hari ${d}: QC sudah diinput (klik untuk hapus tanda)` 
                          : `Hari ${d}: klik untuk tandai QC sudah diinput`;
                        return (
                          <th
                            key={d}
                            className={`w-8 min-w-[32px] max-w-[32px] text-center relative border-b py-2 ${d === todayCol ? 'ls-today' : ''} cursor-pointer hover:bg-primary/10`}
                            data-testid={isMarked ? `day-header-marked-${d}` : `day-header-${d}`}
                            onClick={() => toggleMark(d)}
                          >
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className={`inline-block w-full ${isMarked ? 'font-bold text-emerald-600' : ''}`}>{d}</span>
                              </TooltipTrigger>
                              <TooltipContent><p>{headerTooltip}</p></TooltipContent>
                            </Tooltip>
                            {isMarked && (
                              <span className="absolute left-1/2 top-0.5 h-1.5 w-1.5 -translate-x-1/2 rounded-full bg-emerald-500" data-testid={`day-mark-dot-${d}`} />
                            )}
                          </th>
                        );
                      })}
                      <th className="ls-sum-col border-b border-l py-2 text-right">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="cursor-pointer">QC ✎</span>
                          </TooltipTrigger>
                          <TooltipContent><p>QC Otomatis (atau input manual)</p></TooltipContent>
                        </Tooltip>
                      </th>
                      <th className="ls-sum-col border-b py-2 text-right">Total<br />Pakai</th>
                      <th className="ls-sum-col border-b py-2 text-right">Stok<br />Masuk</th>
                      <th className="ls-sum-col border-b py-2 text-right">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="cursor-pointer hover:bg-primary/5">Sisa<br />Stok ✎</span>
                          </TooltipTrigger>
                          <TooltipContent><p>Klik pada angka sisa stok untuk melakukan Quick Stock Adjust (Koreksi Stok Riil)</p></TooltipContent>
                        </Tooltip>
                      </th>
                      <th className="ls-sum-col border-b py-2 text-right">Buffer</th>
                      <th className="ls-sum-col border-b py-2 text-left">Satuan</th>
                      <th className="ls-sum-col border-b py-2 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const isIndiko = r.nama_reagen && r.nama_reagen.toUpperCase().includes('INDIKO');
                      const computedQc = calculateQcValue(r);
                      const finalQc = isIndiko ? computedQc : (r.qc ?? 0);

                      let totalHarian = 0;
                      for (let day = 1; day <= 31; day++) {
                        const val =
                          (r.hari && r.hari[String(day)]) ||
                          (r.hari && r.hari[day]) ||
                          r[String(day)] ||
                          r[day] ||
                          r[`day_${day}`];
                        totalHarian += Number(val) || 0;
                      }

                      const totalPakaiCalculated = totalHarian + finalQc;
                      const saldoAwal = Number(r.saldo_awal) || 0;
                      const stokMasuk = Number(r.stok_masuk) || 0;

                      // Sisa stok dihitung secara dinamis dari Saldo Awal Sistem yang baru
                      const finalSisaStok = (saldoAwal - totalPakaiCalculated) + stokMasuk;

                      const isAdjusted = r.adjust_info !== undefined && r.adjust_info !== null;
                      const sisaStokTooltip = isAdjusted 
                        ? `Pernah dikoreksi (${r.adjust_info.reason || 'Quick Adjust'}). Klik untuk sesuaikan stok riil.` 
                        : "Klik untuk sesuaikan stok riil kulkas/alat";

                      return (
                        <tr key={r.reagen_id} className={`ls-row ls-row-${r.status}`}>
                          <td className="ls-sticky-col border-b border-r px-3 py-1.5 font-medium">{r.nama_reagen}</td>
                          <EditableNumber
                            value={r.saldo_awal}
                            onSave={(v) => saveSaldo(r, v)}
                            testid={`saldo-awal-${r.reagen_id}`}
                            title="Klik untuk edit Saldo Awal"
                            alignCls="text-center w-20 min-w-[75px] px-2"
                            editable
                          />
                          {dayCols.map((d) => {
                            const v =
                              (r.hari && r.hari[String(d)]) ||
                              (r.hari && r.hari[d]) ||
                              r[String(d)] ||
                              r[d] ||
                              r[`day_${d}`] ||
                              0;
                            return (
                              <EditableDay
                                key={d}
                                value={v}
                                onSave={(val) => saveHari(r, d, val)}
                                testid={`hari-${r.reagen_id}-${d}`}
                                editable
                                isOverridden={Object.prototype.hasOwnProperty.call(r.hari_override || {}, String(d))}
                                isToday={d === todayCol}
                                day={d}
                                isDerived={r.is_derived}
                              />
                            );
                          })}
                          <EditableNumber
                            value={finalQc}
                            onSave={(v) => saveQc(r, v)}
                            testid={`qc-${r.reagen_id}`}
                            alignCls="ls-sum-col text-right border-l"
                            title={isIndiko ? "QC Otomatis Indiko (Dihitung dari hari terisi)" : "Klik untuk input manual QC"}
                            editable={!isIndiko}
                          />
                          <td className="num ls-sum-col border-b py-1.5 text-right font-semibold">{fmtNum(totalPakaiCalculated)}</td>
                          <td className="num ls-sum-col border-b py-1.5 text-right">{fmtNum(r.stok_masuk)}</td>
                          
                          {/* SEL SISA STOK - INTERAKTIF UNTUK QUICK STOCK ADJUST */}
                          <td
                            className={`num ls-sum-col border-b py-1.5 text-right font-semibold group/edit cursor-pointer hover:bg-primary/10 ${
                              r.status === 'critical' ? 'text-red-700' : ''
                            } ${isAdjusted ? 'text-amber-600 font-bold' : ''}`}
                            data-testid={`sisa-stok-${r.reagen_id}`}
                            onClick={() => handleOpenAdjust(r, finalSisaStok)}
                          >
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="inline-flex items-center gap-1 justify-end w-full">
                                  {fmtNum(finalSisaStok)}
                                  <Pencil className="h-3 w-3 opacity-0 text-muted-foreground transition-opacity group-hover/edit:opacity-70" />
                                </span>
                              </TooltipTrigger>
                              <TooltipContent><p>{sisaStokTooltip}</p></TooltipContent>
                            </Tooltip>
                          </td>

                          <td className="num ls-sum-col border-b py-1.5 text-right text-muted-foreground">{fmtNum(r.buffer_stock)}</td>
                          <td className="ls-sum-col border-b py-1.5 text-left text-xs text-muted-foreground">{r.satuan}</td>
                          <td className="ls-sum-col border-b py-1.5 text-center"><StatusBadge status={r.status} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>

            <div className="space-y-1 text-xs text-muted-foreground">
              <p className="flex items-center gap-1.5">
                <Info className="h-3.5 w-3.5" />
                Menampilkan {rows.length} dari {data.total_reagen} reagen. Kolom harian 1-31 bersumber dari file LIS via Pemetaan Test. Sisa Stok (otomatis) = (Saldo Awal - Total Pemakaian) + Stok Masuk. Status {'"Perlu Cek"'} = saldo awal belum tersedia.
              </p>
              <p className="flex items-center gap-1.5">
                <Pencil className="h-3 w-3" />
                Kolom <b>Saldo Awal</b> & <b>QC</b> dapat diklik untuk input manual (khusus reagen Indiko, QC dihitung otomatis per hari terisi). Kolom <b>Sisa Stok</b> dapat diklik untuk melakukan <b>Quick Stock Adjust</b> apabila terjadi selisih fisik riil di kulkas/alat (misal akibat dead volume).
              </p>
            </div>
          </>
        )}

        {/* MODAL / DIALOG POP-UP QUICK STOCK ADJUST (OPSI A - STOK SISTEM) */}
        <Dialog open={adjustOpen} onOpenChange={setAdjustOpen}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-base">
                <SlidersHorizontal className="h-4 w-4 text-primary" />
                Quick Stock Adjust (Koreksi Stok Riil)
              </DialogTitle>
              <DialogDescription className="text-xs">
                Sesuaikan jumlah sisa stok fisik untuk <b>{selectedRow?.nama_reagen}</b> jika menemukan selisih di kulkas/alat.
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-3 py-2 text-sm">
              <div className="flex items-center justify-between bg-muted/50 p-2.5 rounded border text-xs">
                <span className="text-muted-foreground">Hitungan Stok Sistem:</span>
                <span className="font-bold">{selectedRow?.currentCalculatedStock ?? 0} {selectedRow?.satuan || 'Test'}</span>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold">Stok Riil Fisik Terkini:</label>
                <Input
                  type="number"
                  value={realStockVal}
                  onChange={(e) => setRealStockVal(e.target.value)}
                  placeholder="Masukkan jumlah fisik..."
                  className="h-9"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold">Alasan Selisih / Koreksi:</label>
                <select
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-xs outline-none focus:ring-1 focus:ring-ring"
                  value={adjustReason}
                  onChange={(e) => setAdjustReason(e.target.value)}
                >
                  <option value="Dead Volume / Sisa Botol">Dead Volume / Sisa Botol</option>
                  <option value="Wastage / Priming Alat">Wastage / Priming Alat</option>
                  <option value="Repeat Test / QC Re-run">Repeat Test / QC Re-run</option>
                  <option value="Reagen Expired / Rusak">Reagen Expired / Rusak</option>
                  <option value="Koreksi Stok Opname">Koreksi Stok Opname</option>
                </select>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold">Catatan Keterangan (Opsional):</label>
                <Input
                  type="text"
                  value={adjustNote}
                  onChange={(e) => setAdjustNote(e.target.value)}
                  placeholder="Misal: Botol terakhir habis terpakai priming..."
                  className="h-9 text-xs"
                />
              </div>
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" size="sm" onClick={() => setAdjustOpen(false)} disabled={adjustLoading}>
                Batal
              </Button>
              <Button size="sm" onClick={handleSaveAdjust} disabled={adjustLoading}>
                {adjustLoading ? 'Memproses...' : 'Simpan Adjustment'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}
